<template>
  <Overlay id="modalPLPFilters" :isOpen="openModal" @close="() => this.$emit('close')">
    <transition name="fade" mode="in-out">
      <div class="overlay-inner">
        <h3 class="overlay-inner__title -upper">
          {{ $t('Filters') }}
        </h3>
        <div class="overlay-inner__body">
          <ul class="overlay-inner__body--filters" v-if="availableFilters">
            <template v-for="filterName in visibleFilters">
              <!-- TOGGLE FILTERS: -boolean -checkbox -no_dropdown -->
              <li
                v-if="isToggleFilter(filterName)"
                :key="'toggle-' + filterName"
                class="toggle-title"
              >
                <generic-selector
                  context="category"
                  :code="filterName"
                  :id="true"
                  :label="filtersData[filterName].translation"
                />
              </li>

              <!-- DROPDOWN CHECKBOX FILTERS: -checkbox and -selector -->
              <li
                v-else-if="isDropdownFilter(filterName)"
                :key="'dropdown-' + filterName"
              >
                <h3 class="mb-0">
                  <button
                    type="button"
                    class="title font-small-title"
                    :id="'title-' + filterName"
                    :aria-expanded="filtersData[filterName].expanded ? 'true' : 'false'"
                    :aria-controls="'filter-' + filterName"
                    @click="closeFilter(filterName)"
                  >
                    {{ filtersData[filterName].translation }}
                    <span class="accordion-icon" aria-hidden="true"></span>
                  </button>
                </h3>
                <transition-expand>
                  <ul class="filter overlay-inner__body--filter" :class="filtersData[filterName].type" v-show="filtersData[filterName].expanded" :id="'filter-' + filterName" :aria-labelledby="'title-' + filterName">
                    <template v-for="(item, index) in sortBy(availableFilters[filterName], filterName === 'diameter' ? 'label' : 'id')">
                      <generic-selector
                        context="category"
                        v-show="filterName === 'diameter' || index < 5 || filtersData[filterName]['showMore']"
                        :code="filterName"
                        :key="index + '-' + item.id"
                        :id="item.id"
                        :num="filterName === 'diameter'"
                        :label="item.label"
                      />
                    </template>
                    <li
                      v-if="filterName !== 'diameter' && availableFilters[filterName].length > 5 && !filtersData[filterName]['showMore']"
                      class="show-more"
                    >
                      <button
                        @click="filtersData[filterName]['showMore'] = true"
                        class="link-light show-more-button"
                      >
                        <span v-text="$t('More Options')"></span>
                      </button>
                    </li>
                  </ul>
                </transition-expand>
              </li>

              <!-- GROUPED BOOLEAN CHECKBOX FILTERS: -boolean -checkbox with items[] -->
              <li
                v-else-if="isGroupedBooleanFilter(filterName)"
                :key="'grouped-' + filterName"
              >
                <h3 class="mb-0">
                  <button
                    type="button"
                    class="title font-small-title"
                    :id="'title-' + filterName"
                    :aria-expanded="filtersData[filterName].expanded ? 'true' : 'false'"
                    :aria-controls="'filter-' + filterName"
                    @click="closeFilter(filterName)"
                  >
                    {{ filtersData[filterName].translation }}
                    <span class="accordion-icon" aria-hidden="true"></span>
                  </button>
                </h3>
                <transition-expand>
                  <ul class="overlay-inner__body--filter filter -boolean -checkbox" v-show="filtersData[filterName].expanded" :id="'filter-' + filterName" :aria-labelledby="'title-' + filterName">
                    <generic-selector
                      v-for="item in getBooleanFilterVisibleItems(filterName)"
                      :key="'item-' + item.id"
                      context="category"
                      :code="item.id"
                      :id="true"
                      :label="item.translation"
                    />
                  </ul>
                </transition-expand>
              </li>
            </template>

            <h3 v-if="visibleFilters.length === 0" class="no-filter">{{$t('No available filter')}}</h3>
          </ul>
        </div>
        <div class="overlay-inner__footer">
          <button
            type="button"
            class="aside-close -stroke clear btn -block"
            @click="resetAllFilters"
            :disabled="!hasActiveFilters"
            key="clear"
          >
            {{ $t('Remove filters') }}
          </button>
          <button
            type="button"
            class="aside-close btn -block"
            @click="$emit('close')"
          >
            {{ $t('Apply filters') }}
          </button>
        </div>
      </div>
    </transition>
  </Overlay>
</template>

<script>
import { ReadyHook } from 'vue-asyncready'
import { buildFilterProductsQuery } from '@vue-storefront/core/helpers'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import Overlay from './utils/Overlay.vue'
import GenericSelector from 'theme/components/core/GenericSelector'
import Sidebar from '@vue-storefront/core/compatibility/components/blocks/Category/Sidebar'

const ITEM_PER_PAGE = 16

export default {
  props: ['filters', 'openModal'],
  name: 'ModalPLPFilters',
  components: {
    Overlay,
    GenericSelector
  },
  mixins: [Sidebar, ReadyHook],
  data () {
    return {
      loading: false,
      success: false,
      filtersData: {
        // ============================
        // TOGGLE FILTERS (no dropdown)
        // Shown first, type: '-boolean -checkbox -no_dropdown'
        // ============================
        new_reference: {
          translation: this.$t('New reference'),
          expanded: false,
          type: '-boolean -checkbox -no_dropdown',
          showMore: false,
          input: false
        },
        // is_shoppable: {
        //   translation: this.$t('Available to purchase online'),
        //   expanded: false,
        //   type: '-boolean -checkbox -no_dropdown',
        //   showMore: false,
        //   input: false
        // },
        // ============================
        // DROPDOWN CHECKBOX FILTERS
        // Shown right after toggles, type: '-checkbox' or '-selector'
        // Structure with data directly from availableFilters
        // ============================
        bracelet: {
          translation: this.$t('Bracelet'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        diameter: {
          translation: this.$t('Diameter'),
          expanded: false,
          type: '-selector',
          showMore: false,
          input: false
        },
        material: {
          translation: this.$t('Material'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        movement: {
          translation: this.$t('Movement'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        'sizes.keyword': {
          translation: this.$t('Size'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        strap_collection: {
          translation: this.$t('Collections'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        strap_material: {
          translation: this.$t('Material'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        strap_color: {
          translation: this.$t('Color'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        interhorn_case: {
          translation: this.$t('Interhorn Case'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        interhorn_buckle: {
          translation: this.$t('Interhorn Buckle'),
          expanded: false,
          type: '-checkbox',
          showMore: false,
          input: false
        },
        // ============================
        // GROUPED BOOLEAN CHECKBOX FILTERS
        // Type: '-boolean -checkbox' with internal items[] structure
        // Filter data is defined in the items array
        // ============================
        characteristics: {
          translation: this.$t('Features'),
          type: '-boolean -checkbox',
          showMore: false,
          expanded: false,
          items: [
            { id: 'limited_edition', translation: this.$t('Limited edition') },
            { id: 'is_skeleton', translation: this.$t('Skeleton') },
          ]
        },
        complications: {
          translation: this.$t('Complication'),
          type: '-boolean -checkbox',
          showMore: false,
          expanded: false,
          items: [
            { id: 'is_date', translation: this.$t('Date') },
            { id: 'is_moonphase', translation: this.$t('Moonphase') },
            { id: 'is_chronograph', translation: this.$t('Chronograph') },
            { id: 'is_double_tourbillon', translation: this.$t('Double tourbillon') },
            { id: 'is_tachymetric', translation: this.$t('Tachymeter') },
            { id: 'is_tenth', translation: this.$t('1/10th of a second') },
            { id: 'is_hundredth', translation: this.$t('1/100th of a second') },
            { id: 'is_110th_second_chronograph', translation: this.$t('110th second chronograph') },
            { id: 'is_small_second', translation: this.$t('Small second') },
            { id: 'is_triple_calendar', translation: this.$t('Triple calendar') },
            { id: 'is_tourbillon', translation: this.$t('Tourbillon') },
            { id: 'is_instantaneous_big_date', translation: this.$t('Instantaneous big date') },
            { id: 'is_fusee_chainee', translation: this.$t('Fusee chainee') },
            { id: 'is_gyroscopic_escapement', translation: this.$t('Gyroscopic escapement') },
            { id: 'is_central_second', translation: this.$t('Central second') },
            { id: 'is_110th_of_a_second_indicator', translation: this.$t('110th of a second indicator') },
            { id: 'is_flyback', translation: this.$t('Flyback') },
            { id: 'is_1100th_second_chronograph', translation: this.$t('1100th second chronograph') },
          ]
        },
      },
      type: [],
    }
  },
  computed: {
    sortedChildrenCategories () {
      return [...this.categories.children].sort((a, b) => (+a.position > +b.position ? 1 : -1))
    },
    visibleFilters () {
      return Object.keys(this.filtersData).filter(key => this.isVisibleFilter(key))
    },
    localizedUrl () {
      return currentStoreView().url || ''
    }
  },
  watch: {
    activeFilters () {
      for (const key in this.filtersData) {
        this.filtersData[key].input = !!this.activeFilters[key]
      }
    }
  },
  methods: {
    // Checks if the filter is visible (has available data or has internal items)
    isVisibleFilter (filterName) {
      const filter = this.filtersData[filterName]
      if (!filter) return false

      // Toggle and Dropdown filters: must have data in availableFilters
      if (this.isToggleFilter(filterName) || this.isDropdownFilter(filterName)) {
        return this.availableFilters[filterName] && this.availableFilters[filterName].length > 1
      }

      // Grouped boolean filters: must have items
      if (this.isGroupedBooleanFilter(filterName)) {
        return filter.items && filter.items.length > 0
      }

      return false
    },
    // Toggle filters: '-boolean -checkbox -no_dropdown'
    isToggleFilter (filterName) {
      const type = this.filtersData[filterName]?.type
      return type === '-boolean -checkbox -no_dropdown'
    },
    // Dropdown filters: '-checkbox' and '-selector'
    isDropdownFilter (filterName) {
      const type = this.filtersData[filterName]?.type
      return type === '-checkbox' || type === '-selector'
    },
    // Grouped boolean filters: '-boolean -checkbox' with items[]
    isGroupedBooleanFilter (filterName) {
      const filter = this.filtersData[filterName]
      if (!filter?.type || filter.type !== '-boolean -checkbox' || !filter?.items) return false
      // Checks that there is at least one item with available values
      return this.getBooleanFilterVisibleItems(filterName).length > 0
    },
    // Returns the visible items (with values in availableFilters)
    getBooleanFilterVisibleItems (filterName) {
      const filter = this.filtersData[filterName]
      if (!filter?.items) return []
      return filter.items.filter(item => 
        this.availableFilters[item.id] && this.availableFilters[item.id].length > 1
      )
    },
    closeFilter(filterName) {
      this.filtersData[filterName].expanded = !this.filtersData[filterName].expanded
      this.filtersData[filterName].showMore = false
    },
    sortBy (filters, by) {
      return by === 'id' ? this.sortById(filters) : this.sortByLabel(filters)
    },
    sortByLabel (filters) {
      return [...filters].sort((a, b) => {
        return a.label.match(/\d+/)[0] - b.label.match(/\d+/)[0]
      })
    },
    toggleChange (v, filterName) {
      this.$gtm.trackEvent({
        event: 'filterBy',
        value: v,
        filterOption: v ? 'On' : 'Off',
        filterType: filterName
      })
      this.$bus.$emit('filter-changed-category', { attribute_code: filterName, id: true, label: 'Yes' })
    },
    resetAllFilters() {
      // if (this.hasActiveFilters) {
      this.$bus.$emit('filter-reset')
      this.$store.dispatch('category/resetFilters')
      /*      this.$store.dispatch("category/searchProductQuery", {
        filters: this.filters
      }); */
      this.$store.dispatch('category/mergeSearchOptions', {
        perPage: ITEM_PER_PAGE,
        sort: 'position:asc',
        searchProductQuery: buildFilterProductsQuery(
          this.category,
          this.activeFilters
        ),
        current: 0
      })
      this.$store.dispatch(
        'category/products',
        this.getCurrentCategoryProductQuery
      )
      this.$gtm.trackEvent({
        event: 'filterBy',
        filterOption: 'reset'
      })
      // }
      this.dispatchedIntervals = []
      history.replaceState({}, '', this.$route.path)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

#modalPLPFilters {
  justify-content: flex-start;
  position: fixed;

  /deep/ .overlay-content {
    position: relative;
    padding: 10rem 6rem 2rem;

    .overlay-inner {
      display: flex;
      flex-direction: column;
      height: 100%;

      &__title {
        padding-bottom: 4rem;
        margin-bottom: 2.4rem;
      }

      &__body {
        overflow-y: auto;
        margin-bottom: 2rem;

        &--filters {
          border-bottom: 0.2rem solid var(--zenith-grey-light);

          .title {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: space-between;
            text-transform: uppercase;
            padding: 0;
            border: none;
            width: 100%;
            text-align: left;
            background: none;
            color: var(--zenith-theme-primary);

            .accordion-icon {
              --icon-size: 1.2rem;
              margin-right: 0.9rem;
              transform: rotate(-135deg);
              width: var(--icon-size);
              height: var(--icon-size);
              border: solid var(--zenith-theme-primary);
              border-width: 0 0.15rem 0.15rem 0;
            }

            &[aria-expanded="false"] .accordion-icon {
              transform: rotate(45deg);
            }
          }

          .title,
          .toggle-title {
            padding-block: 2.4rem;
            border-top: 0.2rem solid var(--zenith-grey-light);
          }
        }

        .filter {
          &.-boolean {
            position: relative;
            padding-top: 0;

            li {
              display: flex;
              align-items: center;
              gap: 2rem;
            }
          }
          &.-selector {
            position: relative;
            display: flex;
            width: 100%;
            table-layout: fixed;
            justify-content: space-between;

            &:before {
              display: block;
              content: '';
              position: absolute;
              bottom: 0;
              height: 0.1rem;
              width: 100%;
              background: var(--zenith-component-sidebar-selector-background);
            }

            li:not(.show-more) {
              width: 100%;
              display: flex;
              justify-content: space-between;
              margin: 0;

              button,
              a {
                width: auto;
                flex-grow: 1;
                font-size: em(12);
                line-height: 2.2rem;
                position: relative;
                text-align: center;
                vertical-align: top;
                background: none;
                padding: 0.5rem 0 1.4rem;

                &:hover {
                  background: none;
                }

                &:before {
                  display: block;
                  content: '';
                  position: absolute;
                  bottom: 0;
                  left: 50%;
                  width: 1.4rem;
                  height: 1.4rem;
                  background: var(--zenith-theme-primary);
                  border-radius: 100%;
                  transform: translate(-50%, 50%) scale(0);
                  transition: all 0.2s ease-in-out;
                }

                &.active {
                  font-size: em(22);

                  &:before {
                    transform: translate(-50%, 50%) scale(1);
                  }
                }
              }
            }
          }
          & > li:not(.show-more) {
            button,
            a {
              position: relative;
              display: block;
              width: 100%;
              padding: 0.5rem 1rem 0.5rem 3.5rem;
              border: none;
              background: none;
              text-align: left;
              border-radius: 0.4rem;
              transition: all 50ms ease-in-out;
              color: var(--zenith-plp-aside-color);

              @include bp(lg) {
                &:hover,
                &:focus {
                  color: var(--zenith-component-sidebar-filter-text-hover);
                  background: var(--zenith-component-sidebar-filter-background-hover);
                  border-radius: 0.4rem;
                }
              }
            }
          }

          &.-checkbox{
            button:not(.show-more-button),
            a {
              &::before,
              &::after {
                display: block;
                content: '';
                position: absolute;
                top: 50%;
                border-radius: 0.4rem;
              }

              &::before {
                width: 1.8rem;
                height: 1.8rem;
                left: 0.3rem;
                transform: translateY(-50%);
                border: 0.1rem solid var(--zenith-component-sidebar-checkbox-border);
              }

              &::after {
                width: 1rem;
                height: 1rem;
                transform: translateY(-50%) scale(0);
                background: var(--zenith-component-sidebar-checkbox-active);
                border: 0.7rem solid var(--zenith-component-sidebar-checkbox-active);
                transition: all 0.2s ease-in-out;
                left: 0.5rem;
              }

              &:hover {
                &:before {
                  border: 1px solid var(--zenith-component-sidebar-checkbox-hover);
                }
              }

              &.active {
                color: var(--zenith-component-sidebar-checkbox-active);

                &:before {
                  border-color: var(--zenith-component-sidebar-checkbox-active);
                }

                &:after {
                  transform: translateY(-50%) scale(1);
                }
              }
            }
          }
          .show-more-button {
            text-transform: initial;
            font-weight: 300;
            margin-top: 1.6rem;
          }
        }

        &--filter {
          margin-bottom: 2.4rem;
        }

        &--filter,
        &--filters {
          list-style: none;
          padding: 0;
        }
      }

      &__footer {
        border-top: 0.1rem solid var(--zenith-component-background);
        margin-top: auto;
        padding-top: 2rem;
        display: flex;
        gap: 1.5rem;
      }
    }

    .close-btn {
      top: 2rem;
      right: 2rem;
    }

    @include bp(max lg) {
      padding-inline: 2rem;
    }
  }

  &.fade-enter /deep/ .overlay-content,
  &.fade-leave-to /deep/ .overlay-content {
    transform: translateX(-100%);
  }
}
</style>
